// src/utils/managerScorecard.ts
// Pure scoring logic for the Manager Scorecard. No Vue / Firebase imports.

export type MetricKey =
  | 'conv_initiated' | 'peer_feedback'
  | 'reviews_completed' | 'goals_progress' | 'culture_contribution'
  | 'reply_rate' | 'insights_opened' | 'debi'

export type CategoryKey = 'leadership' | 'performance' | 'engagement'

export interface MetricDef {
  key: MetricKey
  label: string   // full label, e.g. 'Conv. Initiated'
  head: string    // short column header, e.g. 'Conv.'
  cat: CategoryKey
  fmt: 'pct' | 'count' | 'num'
  core: boolean   // shown by default in the table (Performance non-core columns collapse)
}

export interface CategoryDef { key: CategoryKey; name: string; color: string }

export const CATEGORIES: CategoryDef[] = [
  { key: 'leadership',  name: 'Leadership',  color: '#6C30CC' },
  { key: 'performance', name: 'Performance', color: '#2f9bd6' },
  { key: 'engagement',  name: 'Engagement',  color: '#00b383' },
]

export const METRICS: MetricDef[] = [
  { key: 'conv_initiated',       label: 'Conv. Initiated', head: 'Conv.',    cat: 'leadership',  fmt: 'pct',   core: true },
  { key: 'peer_feedback',        label: 'Peer Feedback',   head: 'Peer',     cat: 'leadership',  fmt: 'count', core: true },
  { key: 'reviews_completed',    label: 'Boosts on Track', head: 'Boosts',   cat: 'performance', fmt: 'pct',   core: true },
  { key: 'goals_progress',       label: 'Goals Progress',  head: 'Goals',    cat: 'performance', fmt: 'pct',   core: false },
  { key: 'culture_contribution', label: 'Culture Contr.',  head: 'Culture',  cat: 'performance', fmt: 'pct',   core: false },
  { key: 'reply_rate',           label: 'Reply Rate',      head: 'Reply',    cat: 'engagement',  fmt: 'pct',   core: true },
  { key: 'insights_opened',      label: 'Insights Opened', head: 'Insights', cat: 'engagement',  fmt: 'pct',   core: true },
  { key: 'debi',                 label: 'Team DEBI',       head: 'DEBI',     cat: 'engagement',  fmt: 'num',   core: true },
]

export const metricsIn = (cat: CategoryKey): MetricDef[] => METRICS.filter(m => m.cat === cat)
const byKey = (k: MetricKey): MetricDef => METRICS.find(m => m.key === k) as MetricDef

const clamp = (v: number, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v))

export function normalizeMetric(key: MetricKey, raw: any): number | null {
  if (raw === null || raw === undefined || raw === '' || raw === '-') return null
  const m = byKey(key)
  if (m.fmt === 'count') {
    const n = Number(raw)
    if (isNaN(n)) return null
    // Peer Feedback is a small count; 2 is the effective ceiling in practice,
    // so a manager at 2 reads as a full bar / full sub-score.
    return n <= 0 ? 0 : n === 1 ? 65 : 100
  }
  const n = parseFloat(raw)
  if (isNaN(n)) return null
  return clamp(n)
}

// Category weights per preset (order L / P / E), even split within a category.
export const PRESETS: Record<string, Record<CategoryKey, number>> = {
  balanced:    { leadership: 33, performance: 33, engagement: 34 },
  engagement:  { leadership: 30, performance: 20, engagement: 50 },
  performance: { leadership: 25, performance: 50, engagement: 25 },
  people:      { leadership: 50, performance: 20, engagement: 30 },
}

export const PRESET_NAMES: Record<string, string> = {
  balanced: 'Balanced', engagement: 'Engagement-First',
  performance: 'Performance-First', people: 'People Leadership', custom: 'Custom',
}

export type Weights = Record<MetricKey, number>

export function presetToWeights(preset: string): Weights {
  const cw = PRESETS[preset] || PRESETS.balanced
  const w = {} as Weights
  CATEGORIES.forEach(c => {
    const ms = metricsIn(c.key)
    ms.forEach(m => { w[m.key] = cw[c.key] / ms.length })
  })
  return w
}

// Default scoring model used when a company hasn't customised its own. It
// weights only the metrics that are reliable out of the box and sums to 100:
//   Insights Opened is 0 because open-rate tracking depends on email pixels,
//   which are blocked on many devices and clients, so the signal isn't
//   trustworthy. Goals Progress and Culture Contribution are 0 because they
//   come from performance reviews that many teams don't run; a company can
//   weight them from the settings dialog once they capture that data.
export const DEFAULT_WEIGHTS: Weights = {
  conv_initiated: 15, peer_feedback: 15,
  reviews_completed: 25, goals_progress: 0, culture_contribution: 0,
  reply_rate: 25, insights_opened: 0, debi: 20,
}

export interface ScoreOpts { includePerformance: boolean }

const isActive = (m: MetricDef, opts: ScoreOpts) =>
  !(m.cat === 'performance' && !opts.includePerformance)

export type MetricValues = Partial<Record<MetricKey, any>>

export function compositeScore(values: MetricValues, weights: Weights, opts: ScoreOpts): number {
  let num = 0, den = 0
  for (const m of METRICS) {
    if (!isActive(m, opts)) continue
    const w = weights[m.key] || 0
    if (w <= 0) continue
    const sub = normalizeMetric(m.key, values[m.key])
    if (sub === null) continue
    num += sub * w
    den += w
  }
  return den ? num / den : 0
}

export function categoryScore(
  values: MetricValues, cat: CategoryKey, weights: Weights, opts: ScoreOpts,
): number | null {
  if (cat === 'performance' && !opts.includePerformance) return null
  let num = 0, den = 0
  for (const m of metricsIn(cat)) {
    const w = weights[m.key] || 0
    if (w <= 0) continue
    const sub = normalizeMetric(m.key, values[m.key])
    if (sub === null) continue
    num += sub * w
    den += w
  }
  return den ? Math.round(num / den) : null
}

export interface BandDef { key: string; label: string; grade: string; min: number }

export const BANDS: BandDef[] = [
  { key: 'promoter', label: 'Role Model',     grade: 'A', min: 85 },
  { key: 'high',     label: 'Effective',      grade: 'B', min: 70 },
  { key: 'moderate', label: 'Developing',     grade: 'C', min: 55 },
  { key: 'low',      label: 'Needs Support',  grade: 'D', min: 40 },
  { key: 'zero',     label: 'Needs Coaching', grade: 'F', min: 0  },
]

export function band(score: number): BandDef {
  return BANDS.find(b => score >= b.min) as BandDef
}

// ---------------------------------------------------------------------------
// Weight mutation helpers
// ---------------------------------------------------------------------------

const sumKeys = (w: Weights, keys: MetricKey[]) => keys.reduce((s, k) => s + (w[k] || 0), 0)

export function categoryWeight(w: Weights, cat: CategoryKey): number {
  return sumKeys(w, metricsIn(cat).map(m => m.key))
}

// Set a category to `val`% of the total, split evenly within the category;
// the other categories scale proportionally so the vector still sums to 100.
export function setCategoryWeight(w: Weights, cat: CategoryKey, val: number): Weights {
  const out = { ...w }
  val = clamp(val)
  const catMetrics = metricsIn(cat)
  const others = METRICS.filter(m => m.cat !== cat)
  const otherTotal = sumKeys(out, others.map(m => m.key))
  const remaining = 100 - val
  if (otherTotal > 0) others.forEach(m => { out[m.key] = out[m.key] / otherTotal * remaining })
  else others.forEach(m => { out[m.key] = remaining / others.length })
  catMetrics.forEach(m => { out[m.key] = val / catMetrics.length })
  return out
}

// Set one metric to `val`% of the total; every other metric scales proportionally.
export function setMetricWeight(w: Weights, key: MetricKey, val: number): Weights {
  const out = { ...w }
  val = clamp(val)
  const others = METRICS.filter(m => m.key !== key)
  const otherTotal = sumKeys(out, others.map(m => m.key))
  const remaining = 100 - val
  if (otherTotal > 0) others.forEach(m => { out[m.key] = out[m.key] / otherTotal * remaining })
  else others.forEach(m => { out[m.key] = remaining / others.length })
  out[key] = val
  return out
}
